import { memo, useCallback, useMemo } from 'react';

import { type Board } from '@air/api/types';

import BoardThumbnail from '~/components/BoardThumbnail/BoardThumbnail';

export type ItemPickerBoardCardProps = {
  board: Board;
  /** True when clicking toggles selection; false when clicking navigates into the board. */
  isSelectable: boolean;
  isSelected: boolean;
  onClick: (board: Board) => void;
  /** Double-clicking browses into the board (instead of toggling selection twice). */
  onDoubleClick?: (board: Board) => void;
  /** Registers the card element with the picker's cross-section marquee (null on unmount). */
  registerElement: (board: Board, element: HTMLElement | null) => void;
};

export const ItemPickerBoardCard = memo(
  ({ board, isSelectable, isSelected, onClick, onDoubleClick, registerElement }: ItemPickerBoardCardProps) => {
    const handleClick = useCallback(() => {
      onClick(board);
    }, [board, onClick]);

    const handleRegisterElement = useCallback(
      (element: HTMLButtonElement | null) => registerElement(board, element),
      [board, registerElement],
    );

    const handleDoubleClick = useCallback(() => {
      onDoubleClick?.(board);
    }, [board, onDoubleClick]);

    const thumbnailBoard = useMemo(
      () => ({
        title: board.title,
        thumbnails: board.coverImage?.url ? [board.coverImage.url] : board.thumbnails,
      }),
      [board.coverImage?.url, board.thumbnails, board.title],
    );

    return (
      <button
        aria-pressed={isSelectable ? isSelected : undefined}
        // w-full: the gallery box's absolute wrapper doesn't stretch children the way the old grid cell did,
        // and the aspect-square face below sizes itself from the button's width.
        className="group w-full rounded-4 text-left outline-none ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-blue-9"
        data-testid="ITEM_PICKER_BOARD_CARD"
        data-title={board.title}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        ref={handleRegisterElement}
        type="button"
      >
        <div className="relative aspect-square w-full overflow-hidden rounded-4 ads:bg-subtle bg-gray-3">
          <BoardThumbnail
            board={thumbnailBoard}
            className="absolute inset-0"
            height="100%"
            objectFit="cover"
            sizes="220px"
            width="100%"
          />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent px-3 pt-8 pb-3">
            <span className="line-clamp-2 font-semibold text-12 text-white leading-tight">{board.title}</span>
          </div>
          {/* The selection ring draws INSET on an overlay: the virtualized gallery clips at the
              list bounds (an outside ring loses its edges on first/last columns), and an inset
              box-shadow on the face itself would paint underneath the absolutely-positioned
              thumbnail. */}
          {isSelected && (
            <div className="pointer-events-none absolute inset-0 rounded-4 ads:ring-primary ring-2 ring-blue-9 ring-inset" />
          )}
        </div>
      </button>
    );
  },
);

ItemPickerBoardCard.displayName = 'ItemPickerBoardCard';
